import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, usePropertyField, usePropertyAddressUrl, useCollectionBinding, useBlockSlot } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, SectionContextProvider } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import pagesData from '../pages.json';

// ============================================================================
// Office hours helpers (used in mobile menu)
// ============================================================================

interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

const DAY_ORDER: Record<string, number> = {
  MON: 0, TUE: 1, WED: 2, THU: 3, FRI: 4, SAT: 5, SUN: 6,
};

function dayOrderIndex(day: string | null | undefined): number {
  if (!day) return 99;
  const key = day.trim().toUpperCase().slice(0, 3);
  return DAY_ORDER[key] ?? 99;
}

function formatHourTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const [h, m] = trimmed.split(':').map(Number);
  if (isNaN(h)) return trimmed;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${suffix}` : `${hr} ${suffix}`;
}

function formatHoursValue(
  openRaw: string | null | undefined,
  closeRaw: string | null | undefined,
): string {
  const open = (openRaw || '').trim();
  const close = (closeRaw || '').trim();
  const combined = `${open} ${close}`.toLowerCase();
  if (/appointment/.test(combined)) return 'By Appointment Only';
  if (!open && !close) return 'Closed';
  if (/closed/.test(combined) && !/\d/.test(combined)) return 'Closed';
  if (!open || !close) return 'Closed';
  return `${formatHourTime(open)} – ${formatHourTime(close)}`;
}

function formatDay(day: string | null | undefined): string {
  if (!day) return '';
  const upper = day.trim().toUpperCase();
  if (upper.length <= 3) return upper;
  return upper.slice(0, 3);
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
}

interface NavItem {
  label: string;
  href: string;
  // Interactive Map is not a page — it opens a popup that renders the Engrain
  // SightMap block. It carries no href so the header never navigates anywhere.
  isInteractiveMap?: boolean;
  // When present, this item renders as a dropdown: hovering (or focusing) the
  // parent reveals the child links. The parent still links to its own href.
  children?: NavItem[];
}

// The Interactive Map is a popup, not a page. It's injected as a synthetic nav
// entry so it keeps its place in the nav order without existing in pages.json.
// Positioned between Apartments (/floor-plans, position 1) and Amenities
// (/amenities, position 2) — the fractional value slots it between the two
// without disturbing the integer page positions.
const INTERACTIVE_MAP_POSITION = 1.5;

// The Gallery page and the Virtual Tour page are grouped together under a single
// "Gallery and Tours" dropdown: hovering the parent reveals both. The parent sits
// at the Gallery page's position and the standalone Virtual Tour top-level entry
// is folded into the dropdown instead of appearing separately.
const GALLERY_HREF = '/gallery';
const VIRTUAL_TOUR_HREF = '/virtual-tour';
const GALLERY_GROUP_LABEL = 'Gallery and Tours';

// Utility pages that live at real routes (so the SSG generates them with their
// own canonical URL) but should not appear in the top nav — they're reached via
// the footer.
const HEADER_NAV_EXCLUDED_PATHS = new Set(['/contact-us', '/privacy-policy']);

const RAW_NAV_ITEMS: (NavItem & { position: number })[] = [
  ...Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (alias === 'home' || page.path === '/') return false;
      if (page.visible === false) return false;
      if ((page as any).template) return false;
      if (HEADER_NAV_EXCLUDED_PATHS.has(page.path)) return false;
      return true;
    })
    .map(([_, page]) => ({ label: page.name, href: page.path, position: page.position })),
  {
    label: 'Interactive Map',
    href: '',
    isInteractiveMap: true,
    position: INTERACTIVE_MAP_POSITION,
  },
].sort((a, b) => a.position - b.position);

// Build the "Gallery and Tours" dropdown children from whichever of the two pages
// are actually present/visible in the nav, preserving their order.
const galleryChildren: NavItem[] = RAW_NAV_ITEMS.filter(
  (item) => item.href === GALLERY_HREF || item.href === VIRTUAL_TOUR_HREF,
).map(({ position: _position, ...item }) => item);

const NAV_ITEMS: NavItem[] = RAW_NAV_ITEMS
  // Remove the standalone Virtual Tour entry — it now lives inside the dropdown.
  .filter((item) => item.href !== VIRTUAL_TOUR_HREF)
  .map(({ position: _position, ...item }) => {
    // Turn the Gallery entry into the "Gallery and Tours" dropdown parent.
    if (item.href === GALLERY_HREF && galleryChildren.length > 0) {
      return { ...item, label: GALLERY_GROUP_LABEL, children: galleryChildren };
    }
    return item;
  });

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      suggestedFields: [
        'property_mono_white_logo',
        'property_mono_black_logo',
        'property_primary_logo',
        'property_secondary_logo',
      ],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Portal' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const utilityLink1Def: SlotGroupDefinition = {
  label: 'Utility Link 1',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Home' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const utilityLink2Def: SlotGroupDefinition = {
  label: 'Utility Link 2',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.alapts.com/landmark-360' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Landmark 360' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const utilityLink3Def: SlotGroupDefinition = {
  label: 'Utility Link 3',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Mobile phone link — editable phone number + link target, rendered in the
// mobile utility bar and mobile menu contact areas.
const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone',
  fields: {
    number: {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Mobile address link — editable address text + directions URL + link target.
// The URL defaults to nothing here; at render time it falls back to
// usePropertyAddressUrl() (mobile-safe Google Maps URL with place/business/
// address fallbacks).
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address', type: 'text', defaultField: 'property_full_address' },
    url: { label: 'URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Luminance helpers — choose mono logo variant based on primary bg contrast
// ============================================================================

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const clean = hex.replace('#', '');
  if (clean.length !== 3 && clean.length !== 6) return null;
  const full =
    clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const num = parseInt(full, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4),
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  item,
  onNavigate,
  onClick,
}: {
  item: NavItem;
  onNavigate: () => void;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  return (
    <a
      href={item.href}
      onClick={(e) => {
        onClick?.(e);
        if (!e.defaultPrevented) onNavigate();
      }}
      className="block py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_85%,transparent)] hover:text-[var(--vibe-color-text-on-secondary)] transition-colors border-b border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)]"
    >
      {item.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [sightmapOpen, setSightmapOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  const phoneToken = useContentToken('header.phone');
  const fallbackPhone = usePropertyField('property_phone') as string | null;
  const phone = phoneToken || fallbackPhone || '';
  const fullAddress = useContentToken('header.full_address');

  // Engrain SightMap — rendered as a swappable block inside the Interactive Map
  // popover. Defaults to the Engrain Sightmap block (reads the property's
  // custom_engrain_sightmap_url / _id automatically); the editor can swap in any
  // Embeds/Integrations map block (e.g. the ApartmentSync interactive map).
  const interactiveMapSlot = useBlockSlot('header.interactive_map_block', {
    defaultValue: 'engrain-sitemap',
  });

  // The Interactive Map nav button only appears when the property has an Engrain
  // SightMap embed URL configured. Without it, there is nothing to show.
  const engrainSightmapUrl = usePropertyField('custom_engrain_sightmap_url') as string | null;
  const hasInteractiveMap = !!(engrainSightmapUrl && String(engrainSightmapUrl).trim());

  // Self-Guided Tour scheduler URL — editor token first, then the property's
  // custom_self_guided_tour_url field (the token's defaultField). The link is
  // only present when one of those resolves to a URL. Clicking it opens the
  // external booking site in a new tab instead of navigating to an internal page.
  const selfGuidedTourUrl = useContentToken('header.self_guided_tour_url');
  const hasSelfGuidedTour = !!(selfGuidedTourUrl && selfGuidedTourUrl.trim());

  // Append a "Self Guided Tour" link at the very end of the nav when the
  // property has a self-guided-tour URL configured.
  const navItems = [
    ...NAV_ITEMS.filter((item) => !item.isInteractiveMap || hasInteractiveMap),
    ...(hasSelfGuidedTour
      ? [{ label: 'Self Guided Tour', href: '/self-guided-tours' } as NavItem]
      : []),
  ];

  // Close Interactive Map popover on Escape
  useEffect(() => {
    if (!sightmapOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSightmapOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sightmapOpen]);

  // Opens the Interactive Map popover (renders the configured SightMap block).
  const openInteractiveMap = useCallback(() => {
    setMenuOpen(false);
    setSightmapOpen(true);
  }, []);

  // Click handler for nav links — Self-Guided Tour pushes directly to the
  // external scheduler site instead of navigating to the internal page.
  const handleNavClick = useCallback(
    (item: NavItem) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (item.href === '/self-guided-tours' && selfGuidedTourUrl) {
        e.preventDefault();
        setMenuOpen(false);
        window.open(selfGuidedTourUrl, '_blank', 'noopener,noreferrer');
      }
    },
    [selfGuidedTourUrl],
  );

  const logoRaw = useSlotGroup('header.logo', logoSlotDef);

  const monoWhiteLogoUrl = usePropertyField('property_mono_white_logo') as string | null;
  const monoBlackLogoUrl = usePropertyField('property_mono_black_logo') as string | null;

  const [scrolledLogoVariant, setScrolledLogoVariant] = useState<'mono_white' | 'mono_black'>(
    'mono_white',
  );
  useEffect(() => {
    // Once the solid primary-color bar appears on scroll, the logo must contrast
    // against it. Rather than re-derive that decision, read the theme's already
    // computed `--vibe-color-text-on-primary` token (itself the WCAG-contrast pick
    // for the primary color): a dark text color means a light primary → mono black,
    // otherwise → mono white. This keeps the logo a single source of truth with the
    // theme's text-on-primary contrast choice.
    const textOnPrimary = getComputedStyle(document.documentElement)
      .getPropertyValue('--vibe-color-text-on-primary')
      .trim();
    setScrolledLogoVariant(
      getRelativeLuminance(textOnPrimary) < 0.5 ? 'mono_black' : 'mono_white',
    );
  }, []);

  const customLogoUrl: string | null = logoRaw.image || null;
  const transparentLogoUrl = customLogoUrl ?? monoWhiteLogoUrl;
  const scrolledComputedLogoUrl =
    customLogoUrl ?? (scrolledLogoVariant === 'mono_white' ? monoWhiteLogoUrl : monoBlackLogoUrl);
  const useDualLogos =
    !customLogoUrl &&
    !!transparentLogoUrl &&
    !!scrolledComputedLogoUrl &&
    transparentLogoUrl !== scrolledComputedLogoUrl;

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const utilityLink1 = useSlotGroup('header.utility_link_1', utilityLink1Def);
  const utilityLink2 = useSlotGroup('header.utility_link_2', utilityLink2Def);
  const utilityLink3 = useSlotGroup('header.utility_link_3', utilityLink3Def);

  // Mobile phone + address slot groups (rendered in the mobile utility bar and
  // mobile menu contact area). The address URL falls back to the mobile-safe
  // Google Maps URL from usePropertyAddressUrl() when left blank.
  const phoneLink = useSlotGroup('header.phone_link', phoneLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();

  const officeHoursRaw = useCollectionBinding<OfficeHourEntry>('header.office_hours', {
    collection: 'office_hours',
  });
  const officeHours = [...officeHoursRaw].sort(
    (a, b) => dayOrderIndex(a.day) - dayOrderIndex(b.day),
  );

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen || sightmapOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen, sightmapOpen]);

  const googlePlaceUrl = usePropertyField('property_google_place_url') as string | null;
  const directionsUrl =
    googlePlaceUrl ||
    (fullAddress
      ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(String(fullAddress))}`
      : '#');

  const closeMobileMenu = () => setMenuOpen(false);

  // Build utility bar links — only render a link when it has a label
  const utilityLinks = [
    { key: 'u1', group: utilityLink1, def: utilityLink1Def, groupPath: 'header.utility_link_1' as const },
    { key: 'u2', group: residentAccessLink, def: residentAccessLinkDef, groupPath: 'header.resident_access_link' as const },
    { key: 'u3', group: utilityLink2, def: utilityLink2Def, groupPath: 'header.utility_link_2' as const },
    { key: 'u4', group: utilityLink3, def: utilityLink3Def, groupPath: 'header.utility_link_3' as const },
  ].filter((l) => l.group.label || l.group.url);

  const isActive = scrolled || menuOpen;

  return (
    <>
      <header
        ref={headerRef}
        className="fixed left-0 right-0 z-50 w-full"
        style={{
          top: 'var(--offer-banner-height, 0px)',
          transition: 'top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* ============================================================ */}
        {/* Utility Bar — dark strip, right-aligned pipe-separated links  */}
        {/* ============================================================ */}
        <div className="header-utility-bar">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-end h-8">
              <nav
                aria-label="Utility navigation"
                className="hidden md:flex items-center gap-0"
              >
                {utilityLinks.map(({ key, group, def, groupPath }, i) => (
                  <React.Fragment key={key}>
                    {i > 0 && (
                      <span className="mx-3 text-white/30 select-none text-xs">|</span>
                    )}
                    <EditableSlotGroup groupPath={groupPath} definition={def}>
                      <a
                        href={group.url || '#'}
                        target={group._target || '_self'}
                        className="text-[11px] font-semibold tracking-[0.14em] uppercase text-white/75 hover:text-white transition-colors whitespace-nowrap"
                      >
                        {group.label || def.fields.label.defaultValue}
                      </a>
                    </EditableSlotGroup>
                  </React.Fragment>
                ))}
              </nav>

              {/* Mobile utility: just the phone */}
              {phoneLink.number && (
                <EditableSlotGroup
                  groupPath="header.phone_link"
                  definition={phoneLinkDef}
                  className="md:hidden"
                >
                  <a
                    href={`tel:${(phoneLink.number || '').replace(/[^+\d]/g, '')}`}
                    target={phoneLink._target || '_self'}
                    type="call"
                    className="dniphone dniphonehref text-[11px] tracking-wide text-white/80 hover:text-white transition-colors"
                  >
                    {formatPhone(phoneLink.number)}
                  </a>
                </EditableSlotGroup>
              )}
            </div>
          </div>
        </div>

        {/* ============================================================ */}
        {/* Main Nav Bar                                                  */}
        {/* ============================================================ */}
        <div
          className={`main-nav-bar transition-all duration-500 ease-out ${isActive ? 'main-nav-bar--active' : ''}`}
        >
          <div className="relative z-10 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 lg:h-[72px]">

              {/* Logo */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a
                  href="/"
                  className={`header-logo flex-shrink-0 ${isActive ? 'header-logo--active' : ''}`}
                >
                  {useDualLogos ? (
                    <div className="relative h-10 lg:h-12 flex items-center">
                      <VibesitesImage
                        url={transparentLogoUrl!}
                        alt={logoRaw.alt || 'Community Logo'}
                        className={`absolute h-10 lg:h-12 w-auto object-contain transition-opacity duration-500 ease-out ${
                          isActive ? 'opacity-0 pointer-events-none' : 'opacity-100'
                        }`}
                      />
                      <VibesitesImage
                        url={scrolledComputedLogoUrl!}
                        alt={logoRaw.alt || 'Community Logo'}
                        className={`h-10 lg:h-12 w-auto object-contain transition-opacity duration-500 ease-out ${
                          isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'
                        }`}
                      />
                    </div>
                  ) : (transparentLogoUrl || scrolledComputedLogoUrl) ? (
                    <VibesitesImage
                      url={(transparentLogoUrl || scrolledComputedLogoUrl)!}
                      alt={logoRaw.alt || 'Community Logo'}
                      className="h-10 lg:h-12 w-auto object-contain"
                    />
                  ) : (
                    <div
                      className={`header-logo-text font-heading text-xl lg:text-2xl font-bold transition-colors duration-500 ${
                        isActive ? 'header-logo-text-active' : 'text-white'
                      }`}
                    >
                      American Landmark
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop: inline nav links */}
              <nav
                aria-label="Primary navigation"
                className="hidden lg:flex items-center gap-0.5 xl:gap-1 mx-4 xl:mx-8"
              >
                {navItems.map((item) => {
                  if (item.isInteractiveMap) {
                    return (
                      <button
                        key="interactive-map"
                        type="button"
                        onClick={openInteractiveMap}
                        className={`nav-link px-3 py-2 text-sm font-medium whitespace-nowrap bg-transparent border-0 cursor-pointer transition-colors duration-300 ${
                          isActive ? 'nav-link--active' : ''
                        }`}
                      >
                        {item.label}
                      </button>
                    );
                  }
                  if (item.children && item.children.length > 0) {
                    return (
                      <div key={item.href || item.label} className="relative group/dropdown">
                        <a
                          href={item.href}
                          onClick={handleNavClick(item)}
                          className={`nav-link inline-flex items-center gap-1 px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                            isActive ? 'nav-link--active' : ''
                          }`}
                          aria-haspopup="true"
                        >
                          {item.label}
                          <svg
                            className="w-3 h-3 transition-transform duration-200 group-hover/dropdown:rotate-180 group-focus-within/dropdown:rotate-180"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
                          </svg>
                        </a>
                        {/* pt-2 bridges the gap so the mouse never leaves the
                            hover target while moving into the panel */}
                        <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover/dropdown:opacity-100 group-hover/dropdown:visible group-hover/dropdown:translate-y-0 group-focus-within/dropdown:opacity-100 group-focus-within/dropdown:visible group-focus-within/dropdown:translate-y-0 transition-all duration-200 z-20">
                          <div className="nav-dropdown-panel min-w-[13rem] py-2">
                            {item.children.map((child) => (
                              <a
                                key={child.href}
                                href={child.href}
                                onClick={handleNavClick(child)}
                                className="nav-dropdown-link block px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors"
                              >
                                {child.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      onClick={handleNavClick(item)}
                      className={`nav-link px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                        isActive ? 'nav-link--active' : ''
                      }`}
                    >
                      {item.label}
                    </a>
                  );
                })}
              </nav>

              {/* Right side: CTA buttons (desktop) + hamburger (mobile) */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Desktop CTA buttons */}
                <div className="hidden lg:flex items-center gap-2">
                  <EditableSlotGroup
                    groupPath="header.apply_now_link"
                    definition={applyNowLinkDef}
                  >
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      className={`header-cta-btn px-5 py-2 text-sm font-semibold tracking-wide whitespace-nowrap transition-all duration-300 ${
                        isActive ? 'header-cta-btn--active' : ''
                      }`}
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>

                  {phone && (
                    <a
                      href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                      type="call"
                      className={`dniphone dniphonehref header-cta-btn px-5 py-2 text-sm font-semibold tracking-wide whitespace-nowrap transition-all duration-300 ${
                        isActive ? 'header-cta-btn--active' : ''
                      }`}
                    >
                      <EditableToken
                        path="header.phone"
                        defaultField="property_call_tracking_phone"
                        defaultValue="(555) 555-5555"
                        as="span"
                      >
                        {formatPhone(phone)}
                      </EditableToken>
                    </a>
                  )}
                </div>

                {/* Mobile hamburger */}
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="lg:hidden relative w-10 h-10 flex items-center justify-center"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`hamburger-line absolute block w-6 h-0.5 transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2'
                    } ${isActive ? 'hamburger-line--active' : ''}`}
                  />
                  <span
                    className={`hamburger-line absolute block w-6 h-0.5 transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    } ${isActive ? 'hamburger-line--active' : ''}`}
                  />
                  <span
                    className={`hamburger-line absolute block w-6 h-0.5 transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2'
                    } ${isActive ? 'hamburger-line--active' : ''}`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================ */}
        {/* Mobile Menu Overlay                                           */}
        {/* ============================================================ */}
        <div
          className={`fixed inset-x-0 bottom-0 mobile-menu-bg overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{
            top: `calc(${headerHeight}px + var(--offer-banner-height, 0px))`,
          }}
        >
          <div className="flex flex-col md:flex-row md:items-start">
            {/* Left column: nav + CTAs */}
            <div className="flex-1 min-w-0 md:border-r md:border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)]">
              <nav role="navigation" aria-label="Primary navigation">
                {navItems.map((item) => {
                  if (item.isInteractiveMap) {
                    return (
                      <button
                        key="interactive-map"
                        type="button"
                        onClick={openInteractiveMap}
                        className="block w-full text-left py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_85%,transparent)] hover:text-[var(--vibe-color-text-on-secondary)] transition-colors border-b border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)]"
                      >
                        {item.label}
                      </button>
                    );
                  }
                  if (item.children && item.children.length > 0) {
                    // No hover on mobile — show the group label, then list the
                    // children as indented links.
                    return (
                      <div key={item.href || item.label}>
                        <div className="py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_55%,transparent)] border-b border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)]">
                          {item.label}
                        </div>
                        {item.children.map((child) => (
                          <a
                            key={child.href}
                            href={child.href}
                            onClick={(e) => {
                              handleNavClick(child)(e);
                              if (!e.defaultPrevented) closeMobileMenu();
                            }}
                            className="block py-3 pl-10 pr-6 font-body text-sm font-medium uppercase tracking-wider text-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_85%,transparent)] hover:text-[var(--vibe-color-text-on-secondary)] transition-colors border-b border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)]"
                          >
                            {child.label}
                          </a>
                        ))}
                      </div>
                    );
                  }
                  return (
                    <MobileNavLink
                      key={item.href}
                      item={item}
                      onNavigate={closeMobileMenu}
                      onClick={handleNavClick(item)}
                    />
                  );
                })}
                {/* Residents — mobile-only entry appended after Self-Guided
                    Tour. The residents portal is a utility page (visible:false
                    in pages.json), so it doesn't appear in the shared NAV_ITEMS
                    list; on mobile we surface it here in the drawer so residents
                    can reach the portal without hunting through the footer.
                    Reuses the same header.resident_access_link slot group as the
                    desktop utility bar so the label/URL is edited in one place. */}
                <EditableSlotGroup
                  groupPath="header.resident_access_link"
                  definition={residentAccessLinkDef}
                >
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    onClick={closeMobileMenu}
                    className="block py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_85%,transparent)] hover:text-[var(--vibe-color-text-on-secondary)] transition-colors border-b border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)]"
                  >
                    {residentAccessLink.label || 'Resident Portal'}
                  </a>
                </EditableSlotGroup>
              </nav>

              <div className="p-5">
                <EditableSlotGroup
                  groupPath="header.schedule_tour_link"
                  definition={scheduleTourLinkDef}
                >
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    onClick={closeMobileMenu}
                    className="nav-cta-btn group/cta relative flex items-center justify-center gap-2 w-full py-3.5 text-sm font-bold tracking-[0.15em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
                  >
                    <span className="relative z-10">
                      {scheduleTourLink.label || 'Schedule a Tour'}
                    </span>
                    <svg
                      className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                      />
                    </svg>
                  </a>
                </EditableSlotGroup>
              </div>

              <div className="px-5 pb-5">
                <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
                  <a
                    href={`tel:${(phoneLink.number || '').replace(/[^+\d]/g, '')}`}
                    target={phoneLink._target || '_self'}
                    type="call"
                    onClick={closeMobileMenu}
                    className="dniphone dniphonehref block text-center py-2.5 text-[11px] font-bold tracking-wider uppercase border border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_30%,transparent)] text-[var(--vibe-color-text-on-secondary)] hover:bg-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)] transition-all rounded-sm"
                  >
                    {formatPhone(phoneLink.number) || 'Call Us'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>

            {/* Right column: contact info */}
            <aside
              className="md:w-80 lg:w-96 md:flex-shrink-0 px-5 py-6 border-t md:border-t-0 border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)] text-right"
              aria-label="Contact information"
            >
              {(addressLink.text || fullAddress) && (
                <div className="mb-5">
                  <h3 className="menu-contact-label">Visit Us</h3>
                  <EditableSlotGroup
                    groupPath="header.address_link"
                    definition={addressLinkDef}
                  >
                    <a
                      href={addressLink.url || propertyAddressUrl || directionsUrl}
                      target={addressLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="menu-contact-value hover:text-[var(--vibe-color-text-on-secondary)] transition-colors"
                    >
                      {addressLink.text || fullAddress}
                    </a>
                  </EditableSlotGroup>
                </div>
              )}

              {phoneLink.number && (
                <div className="mb-5">
                  <h3 className="menu-contact-label">Call Us</h3>
                  <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
                    <a
                      href={`tel:${(phoneLink.number || '').replace(/[^+\d]/g, '')}`}
                      target={phoneLink._target || '_self'}
                      type="call"
                      className="dniphone dniphonehref menu-contact-value hover:text-[var(--vibe-color-text-on-secondary)] transition-colors"
                    >
                      {formatPhone(phoneLink.number)}
                    </a>
                  </EditableSlotGroup>
                </div>
              )}

              {officeHours.length > 0 && (
                <div>
                  <h3 className="menu-contact-label">Office Hours</h3>
                  <dl className="menu-hours-grid">
                    {officeHours.map((h, i) => (
                      <React.Fragment key={i}>
                        <dt className="menu-hours-day">{formatDay(h.day)}</dt>
                        <dd className="menu-hours-time">
                          {formatHoursValue(h.open_time, h.close_time)}
                        </dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </div>
              )}
            </aside>
          </div>
        </div>
      </header>

      {/* ============================================================ */}
      {/* Interactive Map popover — opens when the Interactive Map nav  */}
      {/* link is clicked and renders the configured map block          */}
      {/* (defaults to the Engrain SightMap iframe-embed block).         */}
      {/* ============================================================ */}
      {sightmapOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Interactive Map"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close Interactive Map"
            onClick={() => setSightmapOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm cursor-default"
          />

          {/* Popover panel */}
          <div className="interactive-map-panel relative z-10 w-full max-w-5xl flex flex-col overflow-hidden rounded-lg bg-[var(--vibe-color-background,#ffffff)] shadow-2xl">
            {/* Header row with title + close button */}
            <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-black/10">
              <h2 className="font-heading text-base font-semibold text-[var(--vibe-color-text,#1a1a1a)]">
                Interactive Map
              </h2>
              <button
                type="button"
                onClick={() => setSightmapOpen(false)}
                aria-label="Close Interactive Map"
                className="w-9 h-9 flex items-center justify-center rounded-full text-[var(--vibe-color-text,#1a1a1a)]/70 hover:text-[var(--vibe-color-text,#1a1a1a)] hover:bg-black/5 transition-all duration-200"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Map block — fills the panel exactly so the embedded map's own
                scroll is the only scrollbar (no outer container scroll). */}
            <div className="interactive-map-body relative flex-1 min-h-0 overflow-hidden">
              <EditableToken
                path="header.interactive_map_block"
                type="block"
                label="Interactive Map Block"
                acceptedBlockCategories={['Embeds', 'Integrations']}
                as="div"
                className="h-full"
              >
                {interactiveMapSlot ? (
                  <SectionContextProvider sectionId={interactiveMapSlot.sectionId}>
                    <interactiveMapSlot.BlockComponent
                      sectionId={interactiveMapSlot.sectionId}
                    />
                  </SectionContextProvider>
                ) : (
                  <div className="p-10 text-center font-body text-[var(--vibe-color-text,#1a1a1a)]/60">
                    No interactive map block selected
                  </div>
                )}
              </EditableToken>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* ============================================================ */
        /* Interactive Map popover                                       */
        /* ------------------------------------------------------------ */
        /* Panel height uses dvh (dynamic viewport height) so mobile     */
        /* browser chrome (address bar) doesn't push the panel past the  */
        /* visible viewport; vh is the fallback for older browsers.      */
        /* ============================================================ */
        .interactive-map-panel {
          height: 90vh;
          height: 90dvh;
        }
        /* The map block renders a fixed-height iframe (default 1000px).  */
        /* Absolutely position it to fill the popover body regardless of */
        /* how many wrappers the block nests it in, so there's no outer  */
        /* scroll. min-height:0 stops iOS Safari from auto-inflating the */
        /* iframe to its content height (a known mobile iframe quirk).   */
        .interactive-map-body iframe {
          position: absolute;
          inset: 0;
          width: 100% !important;
          height: 100% !important;
          min-height: 0 !important;
          max-height: none !important;
          border: 0;
        }

        /* ============================================================ */
        /* Utility bar — very dark strip                                 */
        /* ============================================================ */
        .header-utility-bar {
          background-color: #1a1e24;
        }

        /* ============================================================ */
        /* Main nav bar — transparent on hero, solid on scroll           */
        /* ============================================================ */
        .main-nav-bar {
          position: relative;
          background: transparent;
          transition: background 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        /* Readability scrim — a soft dark gradient sits behind the logo and
           nav links while the bar is transparent over the hero, so the white
           text and logo stay legible on light or busy backgrounds. It fades
           out once the bar gains its solid background on scroll. */
        .main-nav-bar::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.72) 0%,
            rgba(0, 0, 0, 0.45) 55%,
            rgba(0, 0, 0, 0) 100%
          );
          opacity: 1;
          transition: opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .main-nav-bar--active {
          background: var(--vibe-color-primary);
        }
        .main-nav-bar--active::before {
          opacity: 0;
        }

        /* ============================================================ */
        /* Logo — soft drop-shadow keeps it legible over busy/light hero */
        /* imagery while the bar is transparent; removed once solid.      */
        /* ============================================================ */
        .header-logo {
          filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.55));
          transition: filter 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .header-logo--active {
          filter: none;
        }

        /* ============================================================ */
        /* Desktop nav links                                             */
        /* ============================================================ */
        .nav-link {
          color: rgba(255, 255, 255, 0.88);
          letter-spacing: 0.02em;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
          transition: color 0.3s, text-shadow 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .nav-link:hover {
          color: #ffffff;
        }
        .nav-link--active {
          color: var(--vibe-color-text-on-primary);
          opacity: 0.88;
          text-shadow: none;
        }
        .nav-link--active:hover {
          opacity: 1;
        }

        /* ============================================================ */
        /* Desktop nav dropdown panel                                    */
        /* ------------------------------------------------------------ */
        /* Solid secondary-color panel so the sub-links stay legible     */
        /* whether the bar is transparent over the hero or solid.        */
        /* ============================================================ */
        .nav-dropdown-panel {
          background: var(--vibe-color-secondary);
          border-radius: 4px;
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28);
          overflow: hidden;
        }
        .nav-dropdown-link {
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 82%, transparent);
        }
        .nav-dropdown-link:hover {
          color: var(--vibe-color-text-on-secondary);
          background: color-mix(in srgb, var(--vibe-color-text-on-secondary) 10%, transparent);
        }

        /* ============================================================ */
        /* CTA outlined buttons in main nav                             */
        /* ============================================================ */
        .header-cta-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 9.5rem;
          box-sizing: border-box;
          border: 1px solid rgba(255, 255, 255, 0.75);
          color: #ffffff;
          background: transparent;
          border-radius: 2px;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
        }
        .header-cta-btn:hover {
          background: #ffffff;
          color: #1a1a1a;
          border-color: #ffffff;
          text-shadow: none;
        }
        .header-cta-btn--active {
          border-color: color-mix(in srgb, var(--vibe-color-text-on-primary) 60%, transparent);
          color: var(--vibe-color-text-on-primary);
          text-shadow: none;
          box-shadow: none;
        }
        .header-cta-btn--active:hover {
          background: var(--vibe-color-text-on-primary);
          color: var(--vibe-color-primary);
          border-color: var(--vibe-color-text-on-primary);
        }

        /* ============================================================ */
        /* Fallback text logo                                            */
        /* ============================================================ */
        .header-logo-text {
          text-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
        }
        .header-logo-text-active {
          color: var(--vibe-color-text-on-primary);
          text-shadow: none;
        }

        /* ============================================================ */
        /* Hamburger lines                                               */
        /* ============================================================ */
        .hamburger-line {
          background-color: white;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
          transition: background-color 0.5s ease, filter 0.5s ease, transform 0.3s ease, opacity 0.3s ease;
        }
        .hamburger-line--active {
          background-color: var(--vibe-color-text-on-primary);
          filter: none;
        }

        /* ============================================================ */
        /* Mobile menu                                                   */
        /* ============================================================ */
        .mobile-menu-bg {
          background: linear-gradient(
            180deg,
            color-mix(in srgb, var(--vibe-color-secondary) 95%, black) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%
          );
        }

        .menu-contact-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 50%, transparent);
          margin: 0 0 0.4rem;
        }
        .menu-contact-value {
          display: block;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 80%, transparent);
          text-decoration: none;
        }
        .menu-hours-grid {
          display: grid;
          grid-template-columns: auto auto;
          justify-content: end;
          column-gap: 1rem;
          row-gap: 0.2rem;
          margin: 0;
        }
        .menu-hours-day {
          font-family: var(--vibe-font-body);
          font-size: 0.825rem;
          font-weight: 600;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 90%, transparent);
          text-align: left;
        }
        .menu-hours-time {
          font-family: var(--vibe-font-body);
          font-size: 0.825rem;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 60%, transparent);
          text-align: right;
          margin: 0;
        }

        /* ============================================================ */
        /* Mobile schedule tour CTA                                      */
        /* ============================================================ */
        .nav-cta-btn {
          background: linear-gradient(
            135deg,
            var(--vibe-color-primary) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%
          );
          border-radius: 9999px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 2px 8px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          top: 0; left: -100%;
          width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .nav-cta-btn:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow: 0 2px 6px rgba(0,0,0,0.15), 0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 35%, transparent);
          transform: translateY(-1px);
        }
        .nav-cta-btn:hover::before { left: 100%; }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }
      `}</style>

      {/* ─── Hidden schema-lint anchors ─────────────────────────────────────── */}
      {/* full_address & self_guided_tour_url are read via useContentToken and    */}
      {/* used to build behaviour (links/URLs) rather than rendered through an    */}
      {/* EditableToken; the utility links render via a map with a variable       */}
      {/* groupPath. These hidden anchors carry the literal paths so the          */}
      {/* source-level linter can find every schema key. They never render.       */}
      <EditableToken
        path="header.full_address"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>
      <EditableToken
        path="header.self_guided_tour_url"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>
      <EditableSlotGroup
        groupPath="header.utility_link_1"
        definition={utilityLink1Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="header.utility_link_2"
        definition={utilityLink2Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="header.utility_link_3"
        definition={utilityLink3Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '(555) 555-5555',
    },
    'header.full_address': {
      label: 'Full Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    'header.interactive_map_block': {
      label: 'Interactive Map Block',
      description: 'Block rendered inside the Interactive Map popover that opens when the "Interactive Map" nav button is clicked. Defaults to the Engrain Sightmap block (reads the property\'s custom_engrain_sightmap_url / custom_engrain_sightmap_id automatically); can be swapped for any Embeds/Integrations map block, e.g. the ApartmentSync interactive map. Note: the nav button only appears when custom_engrain_sightmap_url has a value.',
      type: 'block',
      defaultValue: 'engrain-sitemap',
      acceptedBlockCategories: ['Embeds', 'Integrations'],
    },
    'header.self_guided_tour_url': {
      label: 'Self-Guided Tour Scheduler URL',
      description: 'External booking site for self-guided tours. Defaults to the property\'s custom_self_guided_tour_url. When set, a "Self Guided Tour" link is appended to the end of the primary navigation, and clicking it opens this site in a new tab. Leave empty to hide the link.',
      type: 'url',
      defaultField: 'custom_self_guided_tour_url',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: {
          label: 'Logo Image',
          type: 'image',
          suggestedFields: [
            'property_mono_white_logo',
            'property_mono_black_logo',
            'property_primary_logo',
            'property_secondary_logo',
          ],
        },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Portal' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.utility_link_1': {
      label: 'Utility Link 1',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Home' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.utility_link_2': {
      label: 'Utility Link 2',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: 'https://www.alapts.com/landmark-360' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Landmark 360' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.utility_link_3': {
      label: 'Utility Link 3',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.phone_link': {
      label: 'Phone',
      fields: {
        number: {
          label: 'Phone Number',
          type: 'phone',
          defaultField: 'property_call_tracking_phone',
          suggestedFields: ['property_call_tracking_phone', 'property_phone'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address',
      fields: {
        text: { label: 'Address', type: 'text', defaultField: 'property_full_address' },
        url: { label: 'URL', type: 'url', defaultValue: '' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
